import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SUBCATEGORIES, CITIES } from "@/constants/categories";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const GREECE_CENTER = [39.3621, 22.942];

const primaryPoint = (b) => {
  const loc = (b.locations && b.locations.find((l) => l.lat != null && l.lng != null));
  if (loc) return { lat: loc.lat, lng: loc.lng, address: loc.address };
  if (b.lat != null && b.lng != null) return { lat: b.lat, lng: b.lng, address: b.address };
  return null;
};

const FitAll = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(points, { padding: [50, 50], maxZoom: 14 });
  }, [JSON.stringify(points)]); // eslint-disable-line
  return null;
};

const MapAllPage = () => {
  const navigate = useNavigate();
  const { t, tt } = useLang();
  const [businesses, setBusinesses] = useState([]);
  const [cat, setCat] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => {
    api.get("/businesses", { params: { sort: "name" } }).then((r) => setBusinesses(r.data)).catch(() => {});
  }, []);

  const markers = useMemo(() => {
    return businesses
      .filter((b) => (cat ? b.subcategory === cat : true))
      .filter((b) => (city ? b.city === city : true))
      .map((b) => ({ b, p: primaryPoint(b) }))
      .filter((x) => x.p);
  }, [businesses, cat, city]);

  const points = markers.map((m) => [m.p.lat, m.p.lng]);

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="map-all-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <MapPin className="text-primary" /> {t("map.title")}
            </h1>
            <p className="text-slate-600 mt-1">{t("map.subtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{t("map.cityLabel")}</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="map-city-select"
                className="h-11 px-4 rounded-xl bg-white border border-slate-200 outline-none focus:ring-2 ring-primary min-w-[12rem] font-semibold"
              >
                <option value="">{t("home.allCities")}</option>
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{t("map.filterLabel")}</label>
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                data-testid="map-category-select"
                className="h-11 px-4 rounded-xl bg-white border border-slate-200 outline-none focus:ring-2 ring-primary min-w-[16rem] font-semibold"
              >
                <option value="">{t("map.allCategories")}</option>
                {Object.values(SUBCATEGORIES).map((s) => (
                  <option key={s.slug} value={s.slug}>{tt(s)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
          <MapContainer center={points[0] || GREECE_CENTER} zoom={12} scrollWheelZoom style={{ height: "70vh", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {markers.map(({ b, p }) => (
              <Marker key={b.id} position={[p.lat, p.lng]}>
                <Popup>
                  <div className="space-y-1">
                    <strong>{b.name}</strong>
                    {p.address ? <div className="text-xs">{p.address}</div> : null}
                    <Button size="sm" className="rounded-full mt-1 h-7 text-xs" onClick={() => navigate(`/activity/${b.id}`)} data-testid="map-popup-view-btn">
                      {t("map.view")}
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
            <FitAll points={points} />
          </MapContainer>
        </div>
        {markers.length === 0 && (
          <p className="text-center text-slate-500 mt-6" data-testid="map-empty">{t("map.empty")}</p>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MapAllPage;
